import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Globe, Menu, X } from "lucide-react";
import { useLang } from "../i18n";
import { useAuth } from "../auth";
import { api, fileUrl } from "../api";

const LANGS = [
    { code: "en", label: "EN" },
    { code: "ht", label: "KR" },
    { code: "fr", label: "FR" }
];

export const Navbar = () => {
    const { t, lang, setLang } = useLang();
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [langOpen, setLangOpen] = useState(false);
    const [logo, setLogo] = useState("");
    const nav = useNavigate();

    useEffect(() => {
        api.get("/settings").then((r) => setLogo(r.data.logo_path || "")).catch(() => {});
    }, []);

    const LINKS = [
        { to: "/", label: t("nav_home") },
        { to: "/about", label: t("nav_about") },
        { to: "/event", label: t("nav_event") },
        { to: "/tickets", label: t("nav_tickets") },
        { to: "/vote", label: t("nav_vote") },
        { to: "/gallery", label: t("nav_gallery") },
        { to: "/journal", label: t("nav_journal") },
        { to: "/contact", label: t("nav_contact") }
    ];

    const brand = logo ? (
        <img src={fileUrl(logo)} alt="LAKOU" className="h-7 object-contain" />
    ) : (
        <span className="font-display font-black text-xl tracking-[0.22em] text-goldgrad">LAKOU</span>
    );

    return (
        <>
            <motion.header
                initial={{ y: -70, opacity: 0, x: "-50%" }}
                animate={{ y: 0, opacity: 1, x: "-50%" }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="fixed top-3 left-1/2 w-[94%] max-w-7xl z-50 backdrop-blur-xl bg-lakou-coal/75 border border-lakou-gold/20 rounded-full px-4 sm:px-6 py-2.5 flex items-center justify-between"
            >
                <Link to="/" data-testid="nav-logo" aria-label="LAKOU home">{brand}</Link>

                <nav className="hidden xl:flex items-center gap-6">
                    {LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                            className={({ isActive }) =>
                                `font-mono2 text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 ${
                                    isActive ? "text-lakou-gold" : "text-lakou-grey hover:text-lakou-cream"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    <div className="relative">
                        <button
                            data-testid="lang-selector"
                            onClick={() => setLangOpen((o) => !o)}
                            className="flex items-center gap-1.5 font-mono2 text-[11px] uppercase tracking-widest text-lakou-grey hover:text-lakou-gold px-2.5 py-2 transition-colors"
                            aria-label="Select language"
                        >
                            <Globe size={14} /> {lang.toUpperCase()}
                        </button>
                        {langOpen && (
                            <div className="absolute right-0 top-full mt-2 rounded-xl border border-lakou-gold/20 bg-lakou-coal shadow-xl overflow-hidden" data-testid="lang-menu">
                                {LANGS.map((l) => (
                                    <button
                                        key={l.code}
                                        data-testid={`lang-option-${l.code}`}
                                        onClick={() => { setLang(l.code); setLangOpen(false); }}
                                        className={`block w-full text-left px-5 py-2.5 font-mono2 text-xs tracking-widest transition-colors ${
                                            lang === l.code ? "text-lakou-gold bg-lakou-raise" : "text-lakou-offwhite hover:bg-lakou-raise"
                                        }`}
                                    >
                                        {l.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {user && (
                        <button onClick={() => nav(user.role === "staff" ? "/checkin" : "/admin")} data-testid="nav-admin-link"
                            className="hidden sm:block font-mono2 text-[11px] uppercase tracking-widest text-lakou-gold px-2 py-2">
                            {user.role === "staff" ? "Check-in" : "Admin"}
                        </button>
                    )}

                    <Link
                        to="/tickets"
                        data-testid="nav-get-tickets"
                        className="hidden sm:inline-flex items-center rounded-full bg-lakou-orange hover:bg-lakou-gold text-white hover:text-lakou-ink font-display font-bold text-xs uppercase tracking-[0.15em] px-5 py-2.5 transition-colors duration-300"
                    >
                        {t("nav_cta")}
                    </Link>

                    <button data-testid="nav-mobile-toggle" onClick={() => setOpen(true)} className="xl:hidden p-2 text-lakou-cream" aria-label="Open menu">
                        <Menu size={20} />
                    </button>
                </div>
            </motion.header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[90] bg-lakou-void/97 backdrop-blur-xl flex flex-col"
                        data-testid="mobile-menu"
                    >
                        <div className="flex items-center justify-between px-5 py-5">
                            <span className="font-display font-black text-xl tracking-[0.22em] text-goldgrad">LAKOU</span>
                            <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu" className="p-2 text-lakou-cream">
                                <X size={24} />
                            </button>
                        </div>
                        <nav className="flex-1 flex flex-col justify-center px-8 gap-1">
                            {LINKS.map((l, i) => (
                                <motion.div key={l.to} initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                                    <NavLink
                                        to={l.to}
                                        onClick={() => setOpen(false)}
                                        className="block font-display font-extrabold text-3xl text-lakou-cream hover:text-lakou-gold py-2 transition-colors"
                                    >
                                        {l.label}
                                    </NavLink>
                                </motion.div>
                            ))}
                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
                                <Link
                                    to="/tickets"
                                    onClick={() => setOpen(false)}
                                    data-testid="mobile-get-tickets"
                                    className="mt-6 inline-flex rounded-full bg-lakou-orange text-white font-display font-bold text-sm uppercase tracking-[0.15em] px-8 py-4"
                                >
                                    {t("nav_cta")}
                                </Link>
                            </motion.div>
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
